// In-process recording adapter. Used in local dev, CI, and E2E so
// tests can assert on the vendor-neutral event stream without making
// network calls or touching `gtag`. Per design §H *Environment
// isolation*, this is the default in non-prod environments.
//
// The buffer is exposed via `recorded()` (snapshot) and `reset()` for
// tests. In E2E, a small bridge wires the buffer onto
// `window.__lf_analytics_recorded__` so Playwright can read it via
// `page.evaluate`.

const MAX_RECORDED_EVENTS = 5000; // bound the buffer so a long-running
// test session doesn't grow unbounded; older events drop off the
// front. 5000 is comfortably above any realistic test scenario.

let buffer = [];

function logRecorded(event) {
    if (typeof console !== 'undefined' && console.debug) {
        console.debug('[ga:recorded]', event.name, event.params);
    }
}

export const memoryAdapter = {
    name: 'memory',
    init(_config) {
        // Wire the buffer onto window so e2e helpers can pull it.
        if (typeof window !== 'undefined') {
            window.__lf_analytics_recorded__ = {
                snapshot: () => buffer.slice(),
                reset: () => {
                    buffer = [];
                },
            };
        }
    },
    track(name, params, context) {
        const event = {
            name,
            params: { ...params },
            user_id: context && context.userId ? context.userId : null,
            user_properties: context && context.userProperties ? { ...context.userProperties } : {},
            timestamp: Date.now(),
        };
        buffer.push(event);
        if (buffer.length > MAX_RECORDED_EVENTS) {
            buffer = buffer.slice(-MAX_RECORDED_EVENTS);
        }
        logRecorded(event);
    },
    identify(_userId, _userProps) {
        // The identity is read off the call-site context at track() time.
        // No-op here — see public/index.js for the wiring.
    },
    setConsent(_state) {
        // No-op; the memory adapter records irrespective of consent state
        // because it never leaves the process. Tests can assert what *would*
        // have fired even before consent is granted (consent gating is
        // enforced by the public API in index.js, not the adapter).
    },
    pageView(routeId, params, context) {
        this.track('page_view', { route_id: routeId, ...params }, context);
    },
    recorded() {
        return buffer.slice();
    },
    reset() {
        buffer = [];
    },
};

// Convenience exports for tests that import directly.
export const recorded = () => memoryAdapter.recorded();
export const reset = () => memoryAdapter.reset();
